import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";

import {
	RATE_MATRIX_HIGHLIGHT_ATTR,
	RATE_MATRIX_HIGHLIGHT_CROSS_CLASS,
	RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
} from "./highlight-class";

import type { DailyRateCell } from "../../../model/types";

type Props = {
	cell: DailyRateCell | null;
	isToday?: boolean;
	isRowHighlighted?: boolean;
	isColumnHighlighted?: boolean;
	onHover?: () => void;
};

const cellClassName = (cell: DailyRateCell | null) => {
	if (!cell) {
		return "bg-zinc-50 text-caption-50";
	}

	switch (cell.status) {
		case "price":
			return "bg-white text-zinc-950";
		case "out_of_period":
			return "bg-pink-50 text-not-entered";
		case "unregistered":
		case "stop_sale":
			return "bg-white text-not-entered";
		case "not_applicable":
			return "bg-zinc-100 text-caption-50";
		default: {
			const _exhaustive: never = cell.status;
			return _exhaustive;
		}
	}
};

export const PlanRateMatrixDailyTableCell = memo(
	({
		cell,
		isToday = false,
		isRowHighlighted = false,
		isColumnHighlighted = false,
		onHover,
	}: Props) => {
		const content =
			cell?.status === "price" && cell.priceYen !== null
				? cell.priceYen.toLocaleString()
				: (cell?.displayLabel ?? "—");
		const isCrossHighlighted = isRowHighlighted && isColumnHighlighted;

		return (
			<td
				className={cn(
					"min-h-14 w-20 min-w-20 border-r border-r-zinc-200 p-1 text-center align-middle text-sm tabular-nums",
					cellClassName(cell),
					isToday && "border-l border-x-zinc-400",
					isCrossHighlighted
						? RATE_MATRIX_HIGHLIGHT_CROSS_CLASS
						: (isRowHighlighted || isColumnHighlighted) && RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
				)}
				{...(isCrossHighlighted
					? { [RATE_MATRIX_HIGHLIGHT_ATTR]: "cross" }
					: isRowHighlighted || isColumnHighlighted
						? {
								[RATE_MATRIX_HIGHLIGHT_ATTR]: isRowHighlighted ? "row" : "column",
							}
						: {})}
				onMouseEnter={onHover}
			>
				{content}
			</td>
		);
	},
);

PlanRateMatrixDailyTableCell.displayName = "PlanRateMatrixDailyTableCell";
